import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { DocumentAPI, type EdgeCiteAnalysisStatusResponse } from '../../../dms/DocumentAPI';
import ClientMatterPicker, { type ClientMatterSelection } from '@/components/shared/ClientMatterPicker';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker&url';

interface DmsDocumentItem {
  document_id: string;
  title: string;
  matter_description?: string | null;
  matter_id?: string | null;
  client_name?: string | null;
  client_id?: string | null;
  updated_at?: string;
  latest_version_id?: string;
}

interface DmsVersionItem {
  version_id: string;
  version_number?: string;
  uploaded_at?: string;
  original_name?: string;
  mime_type?: string;
  size_bytes?: number;
}

interface AttachmentPickerModalProps {
  open: boolean;
  onClose: () => void;
  onAddLocalFiles: (files: File[]) => void;
  onAddDmsSelection: (selection: {
    documentId: string;
    versionId: string;
    title: string;
    versionLabel?: string;
    matterId?: string | null;
    clientId?: string | null;
  }) => Promise<boolean>;
  internalMode: boolean;
  modeHint?: string;
  enableLexPreparation?: boolean;
  requireLexPrepared?: boolean;
}

export default function AttachmentPickerModal({
  open,
  onClose,
  onAddLocalFiles,
  onAddDmsSelection,
  internalMode,
  modeHint,
  enableLexPreparation = false,
  requireLexPrepared = false,
}: AttachmentPickerModalProps) {
  const [tab, setTab] = useState<'local' | 'dms'>('dms');
  const [search, setSearch] = useState('');
  const [docs, setDocs] = useState<DmsDocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DmsDocumentItem | null>(null);
  const [versions, setVersions] = useState<DmsVersionItem[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<Array<{
    documentId: string;
    versionId: string;
    title: string;
    versionLabel?: string;
    matterId?: string | null;
    clientId?: string | null;
  }>>([]);
  const [previewTarget, setPreviewTarget] = useState<{ doc: DmsDocumentItem; version: DmsVersionItem } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [attachProgress, setAttachProgress] = useState<{ total: number; completed: number } | null>(null);
  const [lexReadiness, setLexReadiness] = useState<Record<string, {
    status: EdgeCiteAnalysisStatusResponse['status'] | 'loading' | 'error';
    progress: number;
    message: string;
    jobId: string | null;
    error?: string | null;
  }>>({});
  const [preparingKey, setPreparingKey] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState<{ id: string; name: string } | null>(null);
  const [matterFilter, setMatterFilter] = useState<{ id: string; label: string; clientId: string; clientName: string } | null>(null);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [showMatterPicker, setShowMatterPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  const { useDocumentAPI } = DocumentAPI;
  const {
    listDocuments,
    searchDocuments,
    getDocument,
    downloadDocumentBlob,
    downloadDocumentPreviewBlob,
    triggerEdgeCiteDocumentAnalysis,
    getEdgeCiteDocumentAnalysisStatus,
  } = useDocumentAPI();

  const modeHintText = modeHint ?? (internalMode
    ? 'Internal recipients detected: DMS docs will send as links.'
    : 'External recipients detected: DMS docs will attach as files.');

  useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  useEffect(() => {
    if (!open) return;
    setTab('dms');
    setSearch('');
    setShowClientPicker(false);
    setShowMatterPicker(false);
    setSelectedVersions([]);
    setAttachError(null);
    setIsAttaching(false);
    setAttachProgress(null);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPreviewOpen(false);
      setPreviewTarget(null);
      setLexReadiness({});
      setPreparingKey(null);
    }
  }, [open]);

  const buildMatterLabel = (selection: ClientMatterSelection) => {
    const number = selection.matterNumber ? `${selection.matterNumber}: ` : '';
    const name = selection.matterName || 'Matter';
    return `${number}${name}`.trim();
  };

  const handleClientSelect = (selection: ClientMatterSelection) => {
    setClientFilter({ id: selection.clientId, name: selection.clientName });
    setMatterFilter(null);
    setShowClientPicker(false);
  };

  const handleMatterSelect = (selection: ClientMatterSelection) => {
    setClientFilter({ id: selection.clientId, name: selection.clientName });
    setMatterFilter({
      id: selection.matterId,
      label: buildMatterLabel(selection),
      clientId: selection.clientId,
      clientName: selection.clientName,
    });
    setShowMatterPicker(false);
  };

  const clearClientFilter = () => {
    setClientFilter(null);
    setMatterFilter(null);
  };

  const clearMatterFilter = () => {
    setMatterFilter(null);
  };

  useEffect(() => {
    if (!open || tab !== 'dms') return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const hasFilters = !!clientFilter?.id || !!matterFilter?.id;
        const trimmed = search.trim();

        if (trimmed.length >= 2 && !hasFilters) {
          const result = await searchDocuments({ q: trimmed, limit: 25 });
          const rows = (Array.isArray((result as any)?.documents)
            ? (result as any).documents
            : result?.results || []) as DmsDocumentItem[];
          if (!cancelled) setDocs(rows);
        } else {
          const result = await listDocuments({
            page: 1,
            limit: 25,
            sortBy: 'updated_at',
            sortOrder: 'desc',
            ...(trimmed.length >= 2 ? { search: trimmed } : {}),
            ...(clientFilter?.id ? { clientId: clientFilter.id } : {}),
            ...(matterFilter?.id ? { matterId: matterFilter.id } : {}),
          });
          if (!cancelled) setDocs((result?.documents || []) as DmsDocumentItem[]);
        }
      } catch {
        if (!cancelled) setDocs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const timer = setTimeout(run, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, tab, search, clientFilter?.id, matterFilter?.id, listDocuments, searchDocuments]);

  useEffect(() => {
    if (!open || tab !== 'dms' || !selectedDoc) return;
    let cancelled = false;
    const load = async () => {
      try {
        const detail = await getDocument(selectedDoc.document_id);
        const docVersions = (detail?.versions || []) as DmsVersionItem[];
        if (!cancelled) {
          setVersions(docVersions);
          const defaultVersion =
            docVersions.find((v) => v.version_id === selectedDoc.latest_version_id) || docVersions[0] || null;
          setSelectedVersionId(defaultVersion?.version_id || null);
        }
      } catch {
        if (!cancelled) {
          setVersions([]);
          setSelectedVersionId(null);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, tab, selectedDoc, getDocument]);

  useEffect(() => {
    if (!selectedDoc) return;
    if (!docs.find((doc) => doc.document_id === selectedDoc.document_id)) {
      setSelectedDoc(null);
      setVersions([]);
      setSelectedVersionId(null);
    }
  }, [docs, selectedDoc]);

  const selectedVersion = useMemo(
    () => versions.find((v) => v.version_id === selectedVersionId) || null,
    [versions, selectedVersionId],
  );

  useEffect(() => {
    if (!open) {
      setPreviewTarget(null);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedDoc || !selectedVersion) {
      setPreviewTarget(null);
      return;
    }
    setPreviewTarget({ doc: selectedDoc, version: selectedVersion });
  }, [selectedDoc?.document_id, selectedVersion?.version_id]);

  const selectionKey = (documentId: string, versionId: string) => `${documentId}:${versionId}`;

  const upsertReadiness = (
    documentId: string,
    versionId: string,
    next: {
      status: EdgeCiteAnalysisStatusResponse['status'] | 'loading' | 'error';
      progress: number;
      message: string;
      jobId?: string | null;
      error?: string | null;
    },
  ) => {
    setLexReadiness((prev) => ({
      ...prev,
      [selectionKey(documentId, versionId)]: {
        status: next.status,
        progress: next.progress,
        message: next.message,
        jobId: next.jobId ?? prev[selectionKey(documentId, versionId)]?.jobId ?? null,
        error: next.error ?? null,
      },
    }));
  };

  const loadLexReadiness = async (doc: DmsDocumentItem, ver: DmsVersionItem, jobId?: string | null) => {
    const key = selectionKey(doc.document_id, ver.version_id);
    if (!lexReadiness[key]) {
      upsertReadiness(doc.document_id, ver.version_id, {
        status: 'loading',
        progress: 0,
        message: 'Checking Lex readiness...',
        jobId: jobId ?? null,
      });
    }
    try {
      const status = await getEdgeCiteDocumentAnalysisStatus(doc.document_id, {
        versionId: ver.version_id,
        ...(jobId ? { jobId } : {}),
      });
      upsertReadiness(doc.document_id, ver.version_id, {
        status: status.status,
        progress: status.progress,
        message: status.message,
        jobId: status.job_id ?? jobId ?? null,
      });
    } catch (error) {
      upsertReadiness(doc.document_id, ver.version_id, {
        status: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Unable to check Lex readiness.',
        jobId: jobId ?? null,
        error: error instanceof Error ? error.message : 'Unable to check Lex readiness.',
      });
    }
  };

  const prepareForLex = async (doc: DmsDocumentItem, ver: DmsVersionItem) => {
    const key = selectionKey(doc.document_id, ver.version_id);
    if (preparingKey) return;
    setPreparingKey(key);
    upsertReadiness(doc.document_id, ver.version_id, {
      status: 'loading',
      progress: 5,
      message: 'Queuing Lex preparation...',
      jobId: lexReadiness[key]?.jobId ?? null,
    });
    try {
      const queued = await triggerEdgeCiteDocumentAnalysis(doc.document_id, { versionId: ver.version_id });
      upsertReadiness(doc.document_id, ver.version_id, {
        status: 'queued',
        progress: 5,
        message: queued.message,
        jobId: queued.job_id,
      });
      await loadLexReadiness(doc, ver, queued.job_id);
    } catch (error) {
      upsertReadiness(doc.document_id, ver.version_id, {
        status: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Unable to queue Lex preparation.',
        jobId: null,
        error: error instanceof Error ? error.message : 'Unable to queue Lex preparation.',
      });
    } finally {
      setPreparingKey(null);
    }
  };

  useEffect(() => {
    if (!open || !enableLexPreparation || tab !== 'dms' || !selectedDoc || versions.length === 0) return;
    let cancelled = false;
    versions.forEach((ver) => {
      const key = selectionKey(selectedDoc.document_id, ver.version_id);
      if (!lexReadiness[key] && !cancelled) {
        void loadLexReadiness(selectedDoc, ver);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, enableLexPreparation, tab, selectedDoc?.document_id, versions.map((ver) => ver.version_id).join('|')]);

  useEffect(() => {
    if (!open || !enableLexPreparation || tab !== 'dms' || !selectedDoc) return;
    const activeVersions = versions.filter((ver) => {
      const status = lexReadiness[selectionKey(selectedDoc.document_id, ver.version_id)]?.status;
      return status === 'queued' || status === 'processing' || status === 'loading';
    });
    if (activeVersions.length === 0) return;
    const timer = window.setInterval(() => {
      activeVersions.forEach((ver) => {
        const current = lexReadiness[selectionKey(selectedDoc.document_id, ver.version_id)];
        void loadLexReadiness(selectedDoc, ver, current?.jobId ?? null);
      });
    }, 2500);
    return () => window.clearInterval(timer);
  }, [open, enableLexPreparation, tab, selectedDoc?.document_id, versions.map((ver) => ver.version_id).join('|'), lexReadiness]);

  const isReadyForLex = (documentId: string, versionId: string) => {
    if (!enableLexPreparation) return true;
    return lexReadiness[selectionKey(documentId, versionId)]?.status === 'completed';
  };

  const lexPreparationBlocksAttach = selectedVersions.some(
    (item) => requireLexPrepared && !isReadyForLex(item.documentId, item.versionId),
  );

  type PreviewStatus = 'loading' | 'ready' | 'unsupported' | 'error';
  type PreviewType = 'pdf' | 'image' | 'text';

  const OFFICE_EXTENSIONS = new Set([
    'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf',
  ]);
  const OFFICE_MIME_PREFIXES = [
    'application/vnd.openxmlformats-officedocument',
    'application/vnd.ms-',
    'application/msword',
    'application/vnd.oasis.opendocument',
  ];

  const getExtension = (filename?: string) => (filename ? filename.split('.').pop()?.toLowerCase() || '' : '');
  const isOfficeType = (mimeType?: string, filename?: string) => {
    const normalized = (mimeType || '').toLowerCase();
    if (OFFICE_MIME_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return true;
    const ext = getExtension(filename);
    return OFFICE_EXTENSIONS.has(ext);
  };

  const DmsVersionPreviewModal = ({
    open: modalOpen,
    target,
    onClose,
  }: {
    open: boolean;
    target: { doc: DmsDocumentItem; version: DmsVersionItem } | null;
    onClose: () => void;
  }) => {
    const [status, setStatus] = useState<PreviewStatus>('loading');
    const [previewType, setPreviewType] = useState<PreviewType | null>(null);
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [textPreview, setTextPreview] = useState<string>('');
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
    const [pageCount, setPageCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const pdfRef = useRef<any>(null);
    const renderKeyRef = useRef(0);

    const fileName = target?.version.original_name || target?.doc.title || 'Document';

    useEffect(() => {
      if (!modalOpen || !target) return;
      let cancelled = false;
      setStatus('loading');
      setPreviewType(null);
      setTextPreview('');
      setPdfData(null);
      setObjectUrl(null);
      setPageCount(0);
      setPageNumber(1);

      const loadPreview = async () => {
        try {
          const useOfficePreview = isOfficeType(target.version.mime_type, target.version.original_name);
          const blob = useOfficePreview
            ? await downloadDocumentPreviewBlob(target.doc.document_id, target.version.version_id)
            : await downloadDocumentBlob(target.doc.document_id, target.version.version_id);
          if (cancelled) return;
          const mimeType = useOfficePreview ? 'application/pdf' : (target.version.mime_type || blob.type || '');

          if (mimeType.startsWith('application/pdf')) {
            const data = new Uint8Array(await blob.arrayBuffer());
            setPreviewType('pdf');
            setPdfData(data);
            setStatus('ready');
            return;
          }
          if (mimeType.startsWith('image/')) {
            const url = URL.createObjectURL(blob);
            setPreviewType('image');
            setObjectUrl(url);
            setStatus('ready');
            return;
          }
          if (mimeType.startsWith('text/') || mimeType === 'application/json' || mimeType === 'application/xml') {
            const text = await blob.text();
            setPreviewType('text');
            setTextPreview(text);
            setStatus('ready');
            return;
          }
          setStatus('unsupported');
        } catch {
          if (!cancelled) setStatus('error');
        }
      };

      loadPreview();

      return () => {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        if (pdfRef.current) {
          pdfRef.current.destroy?.();
          pdfRef.current = null;
        }
      };
    }, [modalOpen, target?.doc.document_id, target?.version.version_id]);

    useEffect(() => {
      if (!modalOpen || !pdfData || previewType !== 'pdf') return;
      let cancelled = false;

      const loadPdf = async () => {
        const pdfjsLib = await import('pdfjs-dist');
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
          // Use Vite worker URL to avoid .mjs MIME issues in production
          pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        }
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        setPageNumber(1);
        renderKeyRef.current += 1;
      };

      loadPdf();

      return () => {
        cancelled = true;
      };
    }, [modalOpen, pdfData, previewType]);

    useEffect(() => {
      if (previewType !== 'pdf' || !pdfRef.current || !canvasRef.current || !containerRef.current) return;
      const render = async () => {
        const pdf = pdfRef.current;
        const page = await pdf.getPage(pageNumber);
        const unscaled = page.getViewport({ scale: 1 });
        const maxWidth = Math.max(320, containerRef.current!.clientWidth - 24);
        const scale = Math.min(2, maxWidth / unscaled.width);
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current!;
        const context = canvas.getContext('2d');
        if (!context) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
      };
      render();
    }, [pageNumber, previewType, pdfData, status]);

    useEffect(() => {
      if (!containerRef.current) return;
      const observer = new ResizeObserver(() => {
        renderKeyRef.current += 1;
        setPageNumber((prev) => prev);
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, []);

    if (!modalOpen || !target || !portalRoot) return null;

    return createPortal((
      <div className="fixed inset-0 z-[80] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60" onClick={onClose} />
        <div className="relative w-[72vw] h-[70vh] max-w-4xl bg-[var(--brand-surface-card)] border border-[var(--brand-border)] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--brand-border)]">
            <div>
              <div className="text-sm font-semibold text-[var(--brand-text-primary)]">Preview</div>
              <div className="text-xs text-[var(--brand-text-tertiary)]">{fileName}</div>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] rounded hover:bg-[var(--brand-surface-hover)] transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0 p-4" ref={containerRef}>
            {status === 'loading' && (
              <div className="h-full flex items-center justify-center text-sm text-[var(--brand-text-tertiary)]">
                Loading preview…
              </div>
            )}
            {status === 'unsupported' && (
              <div className="h-full flex items-center justify-center text-sm text-[var(--brand-text-tertiary)]">
                Preview is not available for this file type yet.
              </div>
            )}
            {status === 'error' && (
              <div className="h-full flex items-center justify-center text-sm text-red-400">
                Unable to load preview.
              </div>
            )}
            {status === 'ready' && previewType === 'image' && objectUrl && (
              <div className="h-full flex items-center justify-center">
                <img
                  src={objectUrl}
                  alt={fileName}
                  className="max-h-full max-w-full rounded-xl border border-[var(--brand-border)]"
                />
              </div>
            )}
            {status === 'ready' && previewType === 'text' && (
              <div className="h-full overflow-auto rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-elevated)] p-4">
                <pre className="text-xs text-[var(--brand-text-primary)] whitespace-pre-wrap break-words">{textPreview}</pre>
              </div>
            )}
            {status === 'ready' && previewType === 'pdf' && (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between text-xs text-[var(--brand-text-tertiary)] mb-2">
                  <span>Page {pageNumber} of {pageCount || 1}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                      disabled={pageNumber <= 1}
                      className="p-1 rounded hover:bg-[var(--brand-surface-hover)] disabled:opacity-40"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPageNumber((prev) => Math.min(pageCount || 1, prev + 1))}
                      disabled={pageNumber >= (pageCount || 1)}
                      className="p-1 rounded hover:bg-[var(--brand-surface-hover)] disabled:opacity-40"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto bg-[var(--brand-surface-elevated)] rounded-xl border border-[var(--brand-border)] p-3">
                  <canvas ref={canvasRef} className="mx-auto" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    ), portalRoot);
  };

  const isVersionSelected = (documentId: string, versionId: string) =>
    selectedVersions.some((item) => item.documentId === documentId && item.versionId === versionId);

  const toggleVersionSelection = (doc: DmsDocumentItem, ver: DmsVersionItem) => {
    setSelectedVersions((prev) => {
      const exists = prev.find((item) => item.documentId === doc.document_id && item.versionId === ver.version_id);
      if (exists) {
        return prev.filter((item) => !(item.documentId === doc.document_id && item.versionId === ver.version_id));
      }
      return [
        ...prev,
        {
          documentId: doc.document_id,
          versionId: ver.version_id,
          title: doc.title,
          versionLabel: ver.version_number,
          matterId: doc.matter_id ?? null,
          clientId: doc.client_id ?? null,
        },
      ];
    });
  };

  const removeSelected = (documentId: string, versionId: string) => {
    setSelectedVersions((prev) => prev.filter((item) => !(item.documentId === documentId && item.versionId === versionId)));
  };

  if (!open || !portalRoot) return null;

  return createPortal((
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 lex-overlay-enter" onClick={onClose} />
      <div className="relative w-[80vw] h-[70vh] max-w-6xl bg-[var(--brand-surface-card)] border border-[var(--brand-border)] rounded-2xl shadow-2xl flex flex-col"
        style={{ animation: 'lex-modal-in 200ms cubic-bezier(0.34, 1.56, 0.64, 1) both' }}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--brand-border)]">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-[var(--brand-text-primary)]">Attach</h2>
            <span className="text-xs text-[var(--brand-text-tertiary)]">{modeHintText}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] rounded hover:bg-[var(--brand-surface-hover)] transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-2 border-b border-[var(--brand-border)]">
          <button
            type="button"
            onClick={() => setTab('dms')}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              tab === 'dms'
                ? 'bg-[var(--brand-accent-primary-soft)] text-[var(--brand-accent-primary)]'
                : 'bg-[var(--brand-surface-muted)] text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]'
            }`}
          >
            EdgeDocs
          </button>
          <button
            type="button"
            onClick={() => setTab('local')}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              tab === 'local'
                ? 'bg-[var(--brand-accent-primary-soft)] text-[var(--brand-accent-primary)]'
                : 'bg-[var(--brand-surface-muted)] text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]'
            }`}
          >
            Local Files
          </button>
          {tab === 'dms' && (
            <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
              <div className="relative flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => { setShowClientPicker((prev) => !prev); setShowMatterPicker(false); }}
                  className="px-3 py-1 text-xs font-medium rounded-full text-amber-400 bg-amber-500/[0.12] hover:bg-amber-500/[0.2] transition-colors"
                  title={clientFilter ? `Client ID: ${clientFilter.id}` : 'Filter by client'}
                >
                  {clientFilter ? clientFilter.name : '+ Filter Client'}
                </button>
                {clientFilter && (
                  <button
                    type="button"
                    onClick={clearClientFilter}
                    className="p-1 text-amber-300 hover:text-amber-100 rounded-full hover:bg-amber-500/[0.15]"
                    title="Clear client filter"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
                {showClientPicker && (
                  <div className="absolute right-0 top-full mt-2 z-50">
                    <ClientMatterPicker
                      onSelect={handleClientSelect}
                      onCancel={() => setShowClientPicker(false)}
                      variant="em"
                      clientOnly
                    />
                  </div>
                )}
              </div>
              <div className="relative flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => { setShowMatterPicker((prev) => !prev); setShowClientPicker(false); }}
                  className="px-3 py-1 text-xs font-medium rounded-full text-[var(--brand-accent-primary)] bg-[var(--brand-accent-primary-soft)] hover:bg-[var(--brand-state-selected)] transition-colors"
                  title={matterFilter ? `Matter ID: ${matterFilter.id}` : 'Filter by matter'}
                >
                  {matterFilter ? matterFilter.label : '+ Filter Matter'}
                </button>
                {matterFilter && (
                  <button
                    type="button"
                    onClick={clearMatterFilter}
                    className="p-1 text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] rounded-full hover:bg-[var(--brand-surface-hover)]"
                    title="Clear matter filter"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
                {showMatterPicker && (
                  <div className="absolute right-0 top-full mt-2 z-50">
                    <ClientMatterPicker
                      onSelect={handleMatterSelect}
                      onCancel={() => setShowMatterPicker(false)}
                      variant="em"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 bg-[var(--brand-surface-muted)] border border-[var(--brand-border)] rounded px-2 py-1 min-w-[200px] max-w-[420px] flex-1">
                <MagnifyingGlassIcon className="w-4 h-4 text-[var(--brand-text-tertiary)]" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-xs text-[var(--brand-text-primary)] outline-none"
                  placeholder="Search documents, matters, or clients..."
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 flex">
          {tab === 'local' ? (
            <div className="flex-1 p-6 flex flex-col items-center justify-center text-center">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  const selected = Array.from(e.target.files ?? []);
                  if (selected.length) onAddLocalFiles(selected);
                  e.target.value = '';
                }}
              />
              <div className="border border-dashed border-[var(--brand-border)] rounded-xl p-8 w-full max-w-xl">
                <p className="text-sm text-[var(--brand-text-primary)] mb-2">Drag & drop files here</p>
                <p className="text-xs text-[var(--brand-text-tertiary)] mb-4">or choose files from your device</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 text-xs font-medium rounded bg-[var(--brand-accent-primary)] text-white hover:opacity-90 transition"
                >
                  Browse Files
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="w-[55%] border-r border-[var(--brand-border)] overflow-auto">
                {loading ? (
                  <div className="p-6 text-sm text-[var(--brand-text-tertiary)]">Loading documents...</div>
                ) : (
                  <ul className="divide-y divide-[var(--brand-border)]">
                    {docs.map((doc) => {
                      const active = selectedDoc?.document_id === doc.document_id;
                      return (
                        <li key={doc.document_id}>
                          <button
                            type="button"
                            onClick={() => setSelectedDoc(doc)}
                            className={`w-full text-left px-5 py-3 hover:bg-[var(--brand-surface-hover)] transition-colors ${
                              active ? 'bg-[var(--brand-state-selected)]' : ''
                            }`}
                          >
                            <div className="text-sm text-[var(--brand-text-primary)] font-medium">{doc.title}</div>
                            <div className="text-xs text-[var(--brand-text-tertiary)]">
                              {doc.matter_description || 'No matter'} · {doc.client_name || 'No client'}
                            </div>
                            <div className="text-[10px] text-[var(--brand-text-tertiary)] mt-1">
                              Document ID: {doc.document_id}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                    {!docs.length && (
                      <li className="p-6 text-sm text-[var(--brand-text-tertiary)]">No documents found.</li>
                    )}
                  </ul>
                )}
              </div>
              <div className="flex-1 p-5 flex flex-col">
                <div className="text-sm font-medium text-[var(--brand-text-primary)] mb-2">
                  {selectedDoc ? selectedDoc.title : 'Select a document'}
                </div>
                {selectedDoc && (
                  <div className="text-xs text-[var(--brand-text-tertiary)] mb-4">
                    Document ID: {selectedDoc.document_id}
                  </div>
                )}
                <div className="flex-1 overflow-auto border border-[var(--brand-border)] rounded-lg">
                  <ul className="divide-y divide-[var(--brand-border)]">
                    {versions.map((ver) => {
                      const active = ver.version_id === selectedVersionId;
                      const selected = selectedDoc ? isVersionSelected(selectedDoc.document_id, ver.version_id) : false;
                      const readinessKey = selectedDoc ? selectionKey(selectedDoc.document_id, ver.version_id) : '';
                      const readiness = readinessKey ? lexReadiness[readinessKey] : null;
                      const preparing = preparingKey === readinessKey;
                      const readyForLex = !enableLexPreparation || readiness?.status === 'completed';
                      const readinessStatusLabel =
                        !enableLexPreparation
                          ? null
                          : readiness?.status === 'completed'
                            ? 'Ready for Lex'
                            : readiness?.status === 'queued' || readiness?.status === 'processing'
                              ? `Preparing ${Math.max(5, readiness.progress || 0)}%`
                              : readiness?.status === 'failed' || readiness?.status === 'error'
                                ? 'Preparation failed'
                                : readiness?.status === 'loading'
                                  ? 'Checking Lex readiness'
                                  : 'Needs Lex preparation';
                      return (
                        <li key={ver.version_id}>
                          <button
                            type="button"
                            onClick={() => setSelectedVersionId(ver.version_id)}
                            className={`w-full text-left px-4 py-2 hover:bg-[var(--brand-surface-hover)] transition-colors ${
                              active ? 'bg-[var(--brand-state-selected)]' : ''
                            }`}
                          >
                            <div className="text-xs text-[var(--brand-text-primary)] font-medium">
                              {ver.version_number || 'Version'}
                            </div>
                            <div className="text-[10px] text-[var(--brand-text-tertiary)]">
                              {ver.original_name || 'File'} · {ver.uploaded_at || 'Unknown date'}
                            </div>
                            <div className="text-[10px] text-[var(--brand-text-tertiary)]">
                              Version ID: {ver.version_id}
                            </div>
                            {enableLexPreparation && (
                              <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                readiness?.status === 'completed'
                                  ? 'bg-emerald-500/15 text-emerald-400'
                                  : readiness?.status === 'queued' || readiness?.status === 'processing' || readiness?.status === 'loading'
                                    ? 'bg-sky-500/15 text-sky-300'
                                    : readiness?.status === 'failed' || readiness?.status === 'error'
                                      ? 'bg-rose-500/15 text-rose-300'
                                      : 'bg-amber-500/15 text-amber-300'
                              }`}>
                                {readiness?.status === 'completed' ? (
                                  <CheckCircleIcon className="h-3 w-3" />
                                ) : readiness?.status === 'queued' || readiness?.status === 'processing' || readiness?.status === 'loading' ? (
                                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                                ) : readiness?.status === 'failed' || readiness?.status === 'error' ? (
                                  <ExclamationTriangleIcon className="h-3 w-3" />
                                ) : (
                                  <SparklesIcon className="h-3 w-3" />
                                )}
                                {readinessStatusLabel}
                              </div>
                            )}
                          </button>
                          {selectedDoc && (
                            <div className="px-4 pb-3 flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleVersionSelection(selectedDoc, ver)}
                                disabled={requireLexPrepared && !readyForLex}
                                className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full transition-colors ${
                                  selected
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                                    : requireLexPrepared && !readyForLex
                                      ? 'bg-[var(--brand-surface-muted)] text-[var(--brand-text-tertiary)] opacity-50 cursor-not-allowed'
                                    : 'bg-[var(--brand-surface-muted)] text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]'
                                }`}
                                title={requireLexPrepared && !readyForLex ? 'Prepare this version for Lex before attaching.' : undefined}
                              >
                                {selected ? 'Added' : 'Add Version'}
                              </button>
                              {enableLexPreparation && !readyForLex && (
                                <button
                                  type="button"
                                  onClick={() => void prepareForLex(selectedDoc, ver)}
                                  disabled={preparing || readiness?.status === 'queued' || readiness?.status === 'processing' || readiness?.status === 'loading'}
                                  className="inline-flex items-center gap-1 rounded-full bg-indigo-500/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-300 hover:bg-indigo-500/25 disabled:cursor-not-allowed disabled:opacity-60"
                                  title="Extract text, chunk, embed, and index this version for Lex."
                                >
                                  {preparing || readiness?.status === 'queued' || readiness?.status === 'processing' || readiness?.status === 'loading' ? (
                                    <ArrowPathIcon className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <SparklesIcon className="h-3 w-3" />
                                  )}
                                  Prepare for Lex
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setPreviewTarget({ doc: selectedDoc, version: ver });
                                  setPreviewOpen(true);
                                }}
                                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full bg-[var(--brand-surface-muted)] text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]"
                              >
                                <span className="inline-flex items-center gap-1">
                                  <EyeIcon className="w-3 h-3" /> Preview
                                </span>
                              </button>
                              {enableLexPreparation && readiness?.message && readiness.status !== 'completed' && (
                                <div className="basis-full text-[10px] text-[var(--brand-text-tertiary)]">
                                  {readiness.message}
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                    {!versions.length && (
                      <li className="p-4 text-xs text-[var(--brand-text-tertiary)]">No versions available.</li>
                    )}
                  </ul>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between text-[10px] text-[var(--brand-text-tertiary)]">
                    <span>
                      {selectedVersion
                        ? `Focused: ${selectedVersion.version_number || 'Version'}`
                        : 'Select a version to preview'}
                    </span>
                    <span>{selectedVersions.length} selected</span>
                  </div>
                  {selectedVersions.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedVersions.map((item) => (
                        <span
                          key={selectionKey(item.documentId, item.versionId)}
                          className="flex items-center gap-1 px-2 py-1 text-[10px] bg-[var(--brand-surface-muted)] text-[var(--brand-text-primary)] rounded-full"
                        >
                          <span className="truncate max-w-[180px]">
                            {item.title} {item.versionLabel ? `(${item.versionLabel})` : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSelected(item.documentId, item.versionId)}
                            className="text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {attachError && (
                    <div className="text-[10px] text-red-500">{attachError}</div>
                  )}
                  {lexPreparationBlocksAttach && (
                    <div className="text-[10px] text-amber-400">
                      Prepare each selected EdgeDocs version for Lex before attaching it to this chat.
                    </div>
                  )}
                  {isAttaching && attachProgress && (
                    <div className="space-y-1">
                      <div className="h-1 w-full rounded-full bg-[var(--brand-border)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--brand-accent-primary)] transition-all duration-300"
                          style={{
                            width: `${Math.min(
                              100,
                              Math.round((attachProgress.completed / Math.max(1, attachProgress.total)) * 100),
                            )}%`,
                          }}
                        />
                      </div>
                      <div className="text-[10px] text-[var(--brand-text-tertiary)]">
                        Attaching {attachProgress.completed}/{attachProgress.total}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      disabled={selectedVersions.length === 0 || isAttaching || lexPreparationBlocksAttach}
                      onClick={async () => {
                        if (!selectedVersions.length || isAttaching || lexPreparationBlocksAttach) return;
                        setIsAttaching(true);
                        setAttachError(null);
                        setAttachProgress({ total: selectedVersions.length, completed: 0 });
                        const results = await Promise.all(
                          selectedVersions.map(async (item) => {
                            const ok = await onAddDmsSelection(item);
                            setAttachProgress((prev) =>
                              prev ? { ...prev, completed: prev.completed + 1 } : prev,
                            );
                            return { item, ok };
                          }),
                        );
                        const failed = results.filter((result) => !result.ok).map((result) => result.item);
                        if (failed.length === 0) {
                          setSelectedVersions([]);
                          setAttachProgress(null);
                          onClose();
                        } else {
                          setSelectedVersions(failed);
                          setAttachError('Some attachments failed. Please try again.');
                        }
                        setIsAttaching(false);
                      }}
                      className="px-4 py-2 text-xs font-medium rounded bg-[var(--brand-accent-primary)] text-white disabled:opacity-50"
                    >
                      {isAttaching ? 'Attaching…' : `Attach ${selectedVersions.length || ''} Selected`}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <DmsVersionPreviewModal
        open={previewOpen}
        target={previewTarget}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  ), portalRoot);
}
